import $ from 'jquery';
import '@selectize/selectize';
import '@selectize/selectize/dist/css/selectize.bootstrap3.css';

const getOptionValue = (opt) => opt?.value ?? opt?.id ?? '';
const getOptionName = (opt) =>
    opt?.name ?? opt?.label ?? String(getOptionValue(opt));

const normalizeOptions = (options = []) =>
    options
        .map((opt) => ({
            value: String(getOptionValue(opt)),
            name: getOptionName(opt),
        }))
        .filter((opt) => opt.value !== '');

const getSelect = (elInput) => {
    if (!elInput) return null;
    if (elInput.tagName === 'SELECT') return elInput;
    return elInput.querySelector?.('select') || null;
};

const destroySelectize = (select) => {
    const instance = select?.selectize;
    if (!instance) return;
    try {
        instance.destroy();
    } catch (e) {
        // Trait DOM may already be gone when GrapesJS re-renders the panel
    }
};

const isSelectizeHealthy = (select, elInput) => {
    const instance = select?.selectize;
    const wrapper = instance?.$wrapper?.[0];
    if (!instance || !wrapper) return false;
    return Boolean(
        elInput?.contains?.(wrapper) || select.parentElement?.contains(wrapper)
    );
};

const syncSelectizeValue = (select, trait) => {
    const instance = select?.selectize;
    if (!instance) return;
    const value = trait.getValue();
    const next = value == null ? '' : String(value);
    if (instance.getValue() !== next) {
        instance.setValue(next, true);
    }
};

const fillNativeOptions = (select, trait) => {
    const options = normalizeOptions(trait.get('options') || []);
    const currentValue = trait.getValue();
    const selected = currentValue == null ? '' : String(currentValue);

    select.innerHTML = '';
    options.forEach((opt) => {
        const option = document.createElement('option');
        option.value = opt.value;
        option.textContent = opt.name;
        if (opt.value === selected) option.selected = true;
        select.appendChild(option);
    });
};

const mountSelectize = (select, trait) => {
    if (!select || typeof $(select).selectize !== 'function') return;

    destroySelectize(select);
    fillNativeOptions(select, trait);

    const options = normalizeOptions(trait.get('options') || []);
    const currentValue = trait.getValue();
    const initialValue =
        currentValue == null || currentValue === ''
            ? ''
            : String(currentValue);

    $(select).selectize({
        options,
        items: initialValue ? [initialValue] : [],
        valueField: 'value',
        labelField: 'name',
        searchField: ['name', 'value'],
        create: false,
        maxItems: 1,
        maxOptions: 10000,
        dropdownParent: 'body',
        placeholder: trait.get('placeholder') || 'Suchen…',
        dropdownClass: 'selectize-dropdown gjs-selectize-dropdown',
        onChange(value) {
            if (typeof trait.setValue === 'function') {
                trait.setValue(value);
            }
        },
    });
};

const ensureSelectize = (elInput, trait, attempt = 0) => {
    const select = getSelect(elInput);
    if (!select) return;

    if (isSelectizeHealthy(select, elInput)) {
        syncSelectizeValue(select, trait);
        return;
    }

    mountSelectize(select, trait);

    if (isSelectizeHealthy(select, elInput) || attempt >= 8) return;

    requestAnimationFrame(() => ensureSelectize(elInput, trait, attempt + 1));
};

export default (editor) => {
    const tm = editor.TraitManager;

    tm.addType('selectize', {
        events: {},

        createInput({ trait }) {
            const wrap = document.createElement('div');
            wrap.className = 'gjs-selectize-wrap';
            const select = document.createElement('select');
            fillNativeOptions(select, trait);
            wrap.appendChild(select);

            // GrapesJS renders trait views into a fragment before inserting
            // them, so mount after the panel is actually in the document.
            requestAnimationFrame(() => ensureSelectize(wrap, trait));
            setTimeout(() => ensureSelectize(wrap, trait), 0);

            return wrap;
        },

        onRender({ elInput, trait }) {
            ensureSelectize(elInput, trait);
            requestAnimationFrame(() => ensureSelectize(elInput, trait));
            setTimeout(() => ensureSelectize(elInput, trait), 0);
        },

        onUpdate({ elInput, trait }) {
            const select = getSelect(elInput);
            if (!isSelectizeHealthy(select, elInput)) {
                ensureSelectize(elInput, trait);
                return;
            }
            syncSelectizeValue(select, trait);
        },

        onEvent() {},
    });
};
